import { DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { FuseConfig, FuseConfigService } from '@fuse/services/config';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FusePlatformService } from '@fuse/services/platform';
import { combineLatest, filter, map, Subject, takeUntil } from 'rxjs';
import { CompactLayoutComponent } from './layouts/vertical/compact/compact.component';
import { environment } from 'environments/environment';
import { AdminService } from 'app/core/datas/admin.service';
import { UserDataService } from 'app/core/datas/user-data';
import { Company, Site } from 'app/core/models/base.model';
import { AuthService } from 'app/core/auth/auth.service';

@Component({
    selector: 'admin-layout',
    templateUrl: './admin-layout.component.html',
    styleUrls: ['./admin-layout.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        CompactLayoutComponent
    ]
})
export class AdminLayoutComponent implements OnInit, OnDestroy {
    config: FuseConfig;
    layout: string;
    scheme: 'dark' | 'light';
    theme: string;
    siteid: number;
    companySite: Company;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        @Inject(DOCUMENT) private _document: any,
        private _renderer2: Renderer2,
        private _router: Router,
        private _fuseConfigService: FuseConfigService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fusePlatformService: FusePlatformService,
        private _adminSvc: AdminService,
        private _userSvc: UserDataService,
        private _authSvc: AuthService,
        private _changeDetectorRef: ChangeDetectorRef
    ) {
        this.siteid = Number(this._activatedRoute.snapshot.paramMap.get('sid'));

    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Set the theme and scheme based on the configuration
        this._adminSvc.updateSiteId(this.siteid);
        this.companySite = this._userSvc.getSelectedCompanySite(this.siteid);
        this._adminSvc.updateCompanySite(this.companySite);

        // One-time API sync on layout entry — refreshes appSettings + parent.appSettings
        this._adminSvc.getRemoteCompanySiteById(this.siteid)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((site: Company) => {
                this.companySite = site;
                this._changeDetectorRef.markForCheck();
            });

        setTimeout(() => {
            if (!this.companySite) {
                if (this._authSvc.hasBasicRole('app_owner')) {
                    this._router.navigate(['admin', 'projects']);
                } else if (this._authSvc.hasBasicRole('project_owner')) {
                    if (environment.projectId > 0) {
                        this._router.navigate(['p', environment.projectId]);
                    } else {
                        let project = this._userSvc.getProject();
                        if (project) {
                            this._router.navigate(['p', project.id]);
                        } else {
                            this._router.navigate(['sign-out'])
                        }
                    }
                } else if (this._authSvc.hasBasicRole('company')) {
                    let company = this._userSvc.getCompany();
                    if (company) {
                        this._router.navigate(['c', company.id]);
                    } else {
                        this._router.navigate(['sign-out'])
                    }
                } else if (this._authSvc.hasBasicRole('site')) {
                    let company = this._userSvc.getCompany();
                    if (company) {
                        this._router.navigate(['s', company.id]);
                    } else {
                        this._router.navigate(['sign-out'])
                    }
                }

            }
        }, 200);

        // this._adminSvc.getRemoteSiteById(this.siteid).subscribe();

        // this._adminSvc.site$.subscribe((site) => {
        //     this.site = site;
        // });
        combineLatest([
            this._fuseConfigService.config$,
            this._fuseMediaWatcherService.onMediaQueryChange$(['(prefers-color-scheme: dark)', '(prefers-color-scheme: light)']),
        ]).pipe(
            takeUntil(this._unsubscribeAll),
            map(([config, mql]) => {
                const options = {
                    scheme: config.scheme,
                    theme: config.theme,
                };

                // If the scheme is set to 'auto'...
                if (config.scheme === 'auto') {
                    // Decide the scheme using the media query
                    options.scheme = mql.breakpoints['(prefers-color-scheme: dark)'] ? 'dark' : 'light';
                }

                return options;
            }),
        ).subscribe((options) => {
            // Store the options
            this.scheme = options.scheme;
            this.theme = options.theme;

            // Update the scheme and theme
            this._updateScheme();
            this._updateTheme();
        });

        // Subscribe to config changes
        this._fuseConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config: FuseConfig) => {
                // Store the config
                this.config = config;

                // Update the layout
                this._updateLayout();
            });

        // Subscribe to NavigationEnd event
        this._router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this._unsubscribeAll),
        ).subscribe(() => {
            // Update the layout
            this._updateLayout();
        });

        // Set the app version
        this._renderer2.setAttribute(this._document.querySelector('[ng-version]'), 'version', environment.appVersion);

        // Set the OS name
        this._renderer2.addClass(this._document.body, this._fusePlatformService.osName);

    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the selected layout
     */
    private _updateLayout(): void {
        // Get the current activated route
        let route = this._activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
        }

        // 1. Set the layout from the config
        this.layout = this.config.layout;

        // 2. Get the query parameter from the current route and
        // set the layout and save the layout to the config
        const layoutFromQueryParam = route.snapshot.queryParamMap.get('layout');
        if (layoutFromQueryParam) {
            this.layout = layoutFromQueryParam;
            if (this.config) {
                this.config.layout = layoutFromQueryParam;
            }
        }

        // 3. Iterate through the paths and change the layout as we find
        // a config for it.
        //
        // The reason we do this is that there might be empty grouping
        // paths or componentless routes along the path. Because of that,
        // we cannot just assume that the layout configuration will be
        // in the last path's config or in the first path's config.
        //
        // So, we get all the paths that matched starting from root all
        // the way to the current activated route, walk through them one
        // by one and change the layout as we find the layout config. This
        // way, layout configuration can live anywhere within the path and
        // we won't miss it.
        //
        // Also, this will allow overriding the layout in any time so we
        // can have different layouts for different routes.
        const paths = route.pathFromRoot;
        paths.forEach((path) => {
            // Check if there is a 'layout' data
            if (path.routeConfig && path.routeConfig.data && path.routeConfig.data.layout) {
                // Set the layout
                this.layout = path.routeConfig.data.layout;
            }
        });
    }

    /**
     * Update the selected scheme
     *
     * @private
     */
    private _updateScheme(): void {
        // Remove class names for all schemes
        this._document.body.classList.remove('light', 'dark');

        // Add class name for the currently selected scheme
        this._document.body.classList.add(this.scheme);
    }

    /**
     * Update the selected theme
     *
     * @private
     */
    private _updateTheme(): void {
        // Find the class name for the previously selected theme and remove it
        this._document.body.classList.forEach((className: string) => {
            if (className.startsWith('theme-')) {
                this._document.body.classList.remove(className, className.split('-')[1]);
            }
        });

        // Add class name for the currently selected theme
        this._document.body.classList.add(this.theme);
    }
}
